import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  Statistic,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQualityPlans, useCreateQualityPlan, useUpdateQualityPlan, useApproveQualityPlan } from '../../hooks/useQuality';
import { useProjects } from '../../hooks/useProjects';
import { useWorkOrders } from '../../hooks/useWorkOrder';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { QualityPlan } from '../../services/quality.service';
import { useIsMobile } from '../../hooks/useResponsive';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const QualityPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<QualityPlan | null>(null);
  const [form] = Form.useForm();
  const [approvalForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const isMobile = useIsMobile();

  const { data: plans = [], isLoading, refetch, error } = useQualityPlans();
  const { data: projects = [], isLoading: projectsLoading } = useProjects({ is_active: true });
  const { data: workOrders = [], isLoading: workOrdersLoading } = useWorkOrders();

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 Quality Plans Hook State:');
    console.log('  - Plans data:', plans);
    console.log('  - Plans count:', plans.length);
    console.log('  - Is loading:', isLoading);
    console.log('  - Error:', error);
    console.log('  - Plans array check:', Array.isArray(plans));
    if (plans.length > 0) {
      console.log('  - First plan:', plans[0]);
    }
  }, [plans, isLoading, error]);
  const createMutation = useCreateQualityPlan();
  const updateMutation = useUpdateQualityPlan();
  const approveMutation = useApproveQualityPlan();

  const handleCreate = () => {
    setSelectedPlan(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (plan: QualityPlan) => {
    setSelectedPlan(plan);
    form.setFieldsValue({
      ...plan,
      work_order_id: plan.work_order_id ?? undefined,
      department_id: plan.department_id ?? undefined,
      effective_date: plan.effective_date ? dayjs(plan.effective_date) : null,
      expiry_date: plan.expiry_date ? dayjs(plan.expiry_date) : null,
    });
    setIsModalVisible(true);
  };

  const handleApprove = (plan: QualityPlan) => {
    setSelectedPlan(plan);
    approvalForm.resetFields();
    setIsApprovalModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        project_id: values.project_id || undefined,
        work_order_id: values.work_order_id || undefined,
        effective_date: values.effective_date ? values.effective_date.format('YYYY-MM-DD') : null,
        expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
      };

      if (selectedPlan) {
        await updateMutation.mutateAsync({ id: selectedPlan.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      console.error('Error saving quality plan:', error);
    }
  };

  const handleApproveSubmit = async (values: any) => {
    try {
      await approveMutation.mutateAsync({ id: selectedPlan!.id, notes: values.notes });
      setIsApprovalModalVisible(false);
      approvalForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error approving quality plan:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Draft: 'default',
      'Under Review': 'processing',
      Approved: 'success',
      Active: 'success',
      Superseded: 'warning',
      Cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const filteredPlans = plans.filter((plan) =>
    (plan.plan_number || plan.id || '').toLowerCase().includes(searchText.toLowerCase()) ||
    plan.project?.project_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: ColumnsType<QualityPlan> = [
    {
      title: 'Plan Number',
      dataIndex: 'plan_number',
      key: 'plan_number',
      sorter: (a, b) => (a.plan_number || a.id).localeCompare(b.plan_number || b.id),
      width: isMobile ? 120 : undefined,
      render: (_: string, record: QualityPlan) => record.plan_number?.trim() || record.id,
    },
    {
      title: 'Work Order',
      key: 'work_order',
      render: (_, record) => {
        if (record.work_order) {
          return (
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/manufacturing/work-orders`)}
              style={{ padding: 0 }}
            >
              {record.work_order.work_order_number}
            </Button>
          );
        }
        return <Text type="secondary">-</Text>;
      },
      className: isMobile ? 'ant-table-column-hide-mobile' : '',
      width: isMobile ? undefined : 140,
    },
    {
      title: 'Project',
      key: 'project',
      render: (_, record) => record.project?.project_name || 'N/A',
      className: isMobile ? 'ant-table-column-hide-mobile' : '',
      width: isMobile ? undefined : 200,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: isMobile ? 80 : undefined,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Effective Date',
      dataIndex: 'effective_date',
      key: 'effective_date',
      render: (date: string) => date ? dayjs(date).format(isMobile ? 'DD/MM/YY' : 'DD/MM/YYYY') : 'N/A',
      className: isMobile ? 'ant-table-column-hide-mobile' : '',
      width: isMobile ? undefined : 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? 120 : undefined,
      fixed: isMobile ? 'right' : undefined,
      render: (_, record) => (
        <Space size={isMobile ? 'small' : 'middle'} wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/qms/quality-plans/${record.id}`)}
          >
            {!isMobile && 'View'}
          </Button>
          <Button type="link" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            {!isMobile && 'Edit'}
          </Button>
          {record.status !== 'Approved' && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleApprove(record)}
            >
              {!isMobile && 'Approve'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const stats = {
    total: plans.length,
    approved: plans.filter((p) => p.status === 'Approved').length,
    active: plans.filter((p) => p.status === 'Active').length,
    draft: plans.filter((p) => p.status === 'Draft').length,
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Title level={isMobile ? 3 : 2} style={{ marginBottom: isMobile ? '16px' : '24px' }}>
        Quality Plans
      </Title>

      <Row gutter={isMobile ? 8 : 16} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card>
            <Statistic 
              title="Total Plans" 
              value={stats.total}
              valueStyle={{ fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card>
            <Statistic 
              title="Approved" 
              value={stats.approved} 
              valueStyle={{ color: '#3f8600', fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card>
            <Statistic 
              title="Active" 
              value={stats.active} 
              valueStyle={{ color: '#145A54', fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card>
            <Statistic 
              title="Draft" 
              value={stats.draft} 
              valueStyle={{ color: '#faad14', fontSize: isMobile ? '20px' : '24px' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space 
          direction={isMobile ? 'vertical' : 'horizontal'}
          style={{ 
            marginBottom: 16, 
            width: '100%', 
            justifyContent: isMobile ? 'stretch' : 'space-between' 
          }}
          className={isMobile ? 'ant-space-vertical-mobile' : ''}
        >
          <Search
            placeholder="Search plans..."
            allowClear
            style={{ width: isMobile ? '100%' : 300 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreate}
            block={isMobile}
          >
            {isMobile ? 'Create' : 'Create Quality Plan'}
          </Button>
        </Space>

        {error && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4 }}>
            <strong>Error loading quality plans:</strong> {error instanceof Error ? error.message : 'Unknown error'}
            <br />
            <small>Check browser console (F12) for details</small>
          </div>
        )}

        {!isLoading && filteredPlans.length === 0 && !error && (
          <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
            No quality plans found. Create your first quality plan to get started.
          </div>
        )}

        <Table
          columns={columns}
          dataSource={filteredPlans}
          loading={isLoading}
          rowKey="id"
          pagination={{ 
            pageSize: isMobile ? 5 : 10,
            showSizeChanger: !isMobile,
            showTotal: !isMobile ? (total) => `Total ${total} items` : undefined,
          }}
          scroll={isMobile ? { x: 'max-content' } : undefined}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedPlan ? 'Edit Quality Plan' : 'Create Quality Plan'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={isMobile ? '95%' : 800}
        style={{ top: isMobile ? 20 : undefined }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="project_id"
            label="Project"
            rules={[
              {
                validator: (_, value) => {
                  const workOrderId = form.getFieldValue('work_order_id');
                  if (value || workOrderId) return Promise.resolve();
                  return Promise.reject(new Error('Select at least one of Project or Work Order'));
                },
              },
            ]}
          >
            <Select
              placeholder="Select project (optional if Work Order is selected)"
              showSearch
              allowClear
              loading={projectsLoading}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={projects.map((project) => ({
                value: project.id,
                label: `${project.project_number} - ${project.project_name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="work_order_id" label="Work Order">
            <Select
              placeholder="Select work order (optional)"
              showSearch
              allowClear
              loading={workOrdersLoading}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={workOrders.map((wo: { id: string; work_order_number?: string; item_name?: string }) => ({
                value: wo.id,
                label: `${wo.work_order_number || wo.id} - ${wo.item_name || 'Work Order'}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="version" label="Version" initialValue="1.0">
            <Input />
          </Form.Item>
          <Form.Item name="effective_date" label="Effective Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Approval Modal */}
      <Modal
        title="Approve Quality Plan"
        open={isApprovalModalVisible}
        onCancel={() => {
          setIsApprovalModalVisible(false);
          approvalForm.resetFields();
        }}
        onOk={() => approvalForm.submit()}
      >
        <Form form={approvalForm} layout="vertical" onFinish={handleApproveSubmit}>
          <Form.Item name="notes" label="Approval Notes">
            <TextArea rows={4} placeholder="Enter approval notes (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QualityPlansPage;

